import { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, FloppyDisk, Bicycle } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'

async function saveDataToSupabase(projectId, data) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr || !user) return
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Transporte',
      criteria_code: 'TRA1-DATA',
      criteria_name: 'TRA1 Datos',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(data),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('saveData error:', error.message)
  } catch (e) { console.error('saveData exception:', e.message) }
}

async function loadDataFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'TRA1-DATA').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) {}
  return null
}

// ── Tipos de transporte (NA3 manual BREEAM ES NC 2015)
// Fórmula oficial (Calculadora BREEAM ES TRA 1 .xls, sheet "Tra1 - Provision
// Public Trans" — verificada contra Qgat 2026-06-04):
//   Por servicio j:
//     T_caminando = distancia / WALKING_SPEED          (min)
//     T_progradado = 30 / F_j                          (min, half-headway)
//     T_medio_espera = T_programado + Factor_fiab      (min)
//     T_acceso_total = T_caminando + T_medio_espera    (min)
//     Frec_umbral_equiv = 60 / (2 × T_acceso_total)
//   Por modo (agrupando servicios de todos los nodos del mismo tipo):
//     Total_EDF_modo = max(Frec_umbral) + 0.5 × sum(resto)   (rank logic)
//   IA_total = sum(Total_EDF_modo) sobre todos los modos
// La fórmula previa IA = K × ∛(F/d) era una simplificación inexacta.
const WALKING_SPEED = 80   // m/min (BREEAM ES NA3)
const TRANSPORT_TYPES = [
  { label: 'Autobús urbano',       factor: 2,    maxDist: 650  },
  { label: 'Autobús interurbano',  factor: 2,    maxDist: 650  },
  { label: 'Tranvía',              factor: 1,    maxDist: 1000 },
  { label: 'Metro',                factor: 0.75, maxDist: 1000 },
  { label: 'Cercanías / Tren',     factor: 0.75, maxDist: 1000 },
  { label: 'Ferry',                factor: 2,    maxDist: 1000 },
]

// Frecuencia umbral equivalente para un servicio (BREEAM ES NA3)
function calcFrecUmbral(distancia, frecHora, factorFiab) {
  if (!frecHora || frecHora <= 0 || !distancia || distancia <= 0) return 0
  const tCam = distancia / WALKING_SPEED
  const tProg = 30 / frecHora
  const tMedio = tProg + factorFiab
  const tAcceso = tCam + tMedio
  return 60 / (2 * tAcceso)
}

// Rank logic por modo: el mayor cuenta entero, el resto a la mitad.
function rankSum(frecs) {
  if (!frecs.length) return 0
  const sorted = [...frecs].sort((a, b) => b - a)
  return sorted[0] + sorted.slice(1).reduce((s, v) => s + v * 0.5, 0)
}

const BUILDING_TYPES = ['Tipo 1 y 2', 'Tipo 3', 'Tipo 4 y 5', 'Tipo 7']

// Tabla 25 — Índice de accesibilidad y puntos disponibles (BREEAM ES NC 2015)
const SCORING_TABLE = {
  'Tipo 1 y 2': [[8, 3], [4, 2], [2, 1]],
  'Tipo 3':     [[18, 5], [12, 4], [8, 3], [4, 2], [2, 1]],
  'Tipo 4 y 5': [[4, 2], [2, 1]],
  'Tipo 7':     [[18, 4], [8, 3], [4, 2], [2, 1]],
}

// Etiquetas visibles de la escala por tipo (umbrales con cambio real de puntos)
const SCALE_LABELS = {
  'Tipo 1 y 2': [[2,1],[4,2],[8,3]],
  'Tipo 3':     [[2,1],[4,2],[8,3],[12,4],[18,5]],
  'Tipo 4 y 5': [[2,1],[4,2]],
  'Tipo 7':     [[2,1],[4,2],[8,3],[18,4]],
}

function getIAPoints(ia, tipo) {
  const table = SCORING_TABLE[tipo] || []
  for (const [thr, pts] of table) {
    if (ia >= thr) return pts
  }
  return 0
}

function newNodo(id) {
  return { id, tipo: 'Autobús urbano', distancia: '', servicios: Array(10).fill('') }
}

export default function TRA1Calculator({ projectId, onSave, saving }) {
  const storageKey = `tra1_${projectId}`
  const [savedOk, setSavedOk] = useState(false)

  // Lazy initializers: carga desde localStorage en el primer render (síncrono)
  // para evitar la race condition entre el load effect y el persist effect
  const [tipoEdificio, setTipoEdificio] = useState(() => {
    if (!projectId) return 'Tipo 3'
    try { return JSON.parse(localStorage.getItem(`tra1_${projectId}`) || 'null')?.tipoEdificio || 'Tipo 3' } catch { return 'Tipo 3' }
  })
  const [autobusDedicado, setAutobusDedicado] = useState(() => {
    if (!projectId) return false
    try { return JSON.parse(localStorage.getItem(`tra1_${projectId}`) || 'null')?.autobusDedicado ?? false } catch { return false }
  })
  const [nodos, setNodos] = useState(() => {
    if (!projectId) return [newNodo(1)]
    try {
      const saved = JSON.parse(localStorage.getItem(`tra1_${projectId}`) || 'null')
      return saved?.nodos?.length ? saved.nodos : [newNodo(1)]
    } catch { return [newNodo(1)] }
  })
  const [nextId, setNextId] = useState(() => {
    if (!projectId) return 2
    try { return JSON.parse(localStorage.getItem(`tra1_${projectId}`) || 'null')?.nextId || 2 } catch { return 2 }
  })

  // Ref-guard: bloquea el persist hasta que el load de Supabase termine.
  // Sin esto el useEffect de persist se dispara con el estado inicial (localStorage
  // o defaults) y pisa los datos remotos antes de que el load asíncrono termine.
  const hasLoaded = useRef(false)

  // Cargar desde Supabase al montar
  useEffect(() => {
    if (!projectId) return
    loadDataFromSupabase(projectId).then(remote => {
      if (remote) {
        if (remote.tipoEdificio) setTipoEdificio(remote.tipoEdificio)
        if (remote.autobusDedicado !== undefined) setAutobusDedicado(remote.autobusDedicado)
        if (remote.nodos?.length) setNodos(remote.nodos)
        if (remote.nextId) setNextId(remote.nextId)
      }
      hasLoaded.current = true
    })
  }, [projectId]) // eslint-disable-line

  // Persistir: localStorage inmediato + Supabase debounced 1.5s (BUG-009)
  const supabaseSaveTimer = useRef(null)
  useEffect(() => {
    if (!projectId) return
    if (!hasLoaded.current) return
    const data = { tipoEdificio, autobusDedicado, nodos, nextId }
    localStorage.setItem(storageKey, JSON.stringify(data))
    if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current)
    supabaseSaveTimer.current = setTimeout(() => saveDataToSupabase(projectId, data), 1500)
    return () => { if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current) }
  }, [tipoEdificio, autobusDedicado, nodos, nextId, projectId]) // eslint-disable-line

  // Flush al desmontar
  useEffect(() => {
    return () => {
      if (!projectId) return
      if (!hasLoaded.current) return
      const data = { tipoEdificio, autobusDedicado, nodos, nextId }
      localStorage.setItem(storageKey, JSON.stringify(data))
      if (supabaseSaveTimer.current) {
        clearTimeout(supabaseSaveTimer.current)
        saveDataToSupabase(projectId, data)
      }
    }
  }, [projectId, tipoEdificio, autobusDedicado, nodos, nextId]) // eslint-disable-line

  function saveNow() {
    const data = { tipoEdificio, autobusDedicado, nodos, nextId }
    localStorage.setItem(storageKey, JSON.stringify(data))
    saveDataToSupabase(projectId, data)
    setSavedOk(true)
    setTimeout(() => setSavedOk(false), 2000)
  }

  // ── Cálculo IA (fórmula oficial BREEAM ES NA3, ver constantes arriba)
  const calcIA = useCallback(() => {
    // 1) Agrupar frec_umbral por modo (todos los servicios de todos los nodos
    //    del mismo tipo se ponen en la misma "bolsa" para la rank logic).
    const byMode = new Map()  // tipo → [frec_umbral, ...]
    for (const nodo of nodos) {
      const d = parseFloat(nodo.distancia)
      if (!d || d <= 0) continue
      const transport = TRANSPORT_TYPES.find(t => t.label === nodo.tipo) || TRANSPORT_TYPES[0]
      if (d > transport.maxDist) continue
      for (const fStr of nodo.servicios) {
        const f = parseFloat(fStr)
        if (!f || f <= 0) continue
        const fu = calcFrecUmbral(d, f, transport.factor)
        if (!byMode.has(nodo.tipo)) byMode.set(nodo.tipo, [])
        byMode.get(nodo.tipo).push(fu)
      }
    }
    // 2) Aplicar rank logic por modo y sumar
    let ia = 0
    for (const frecs of byMode.values()) ia += rankSum(frecs)
    return ia
  }, [nodos])

  const ia    = calcIA()
  const iaPts = getIAPoints(ia, tipoEdificio)
  const pts   = iaPts === 0 && autobusDedicado ? 1 : iaPts
  const hasBusBonus = iaPts === 0 && autobusDedicado

  // ── Mutations
  const updateNodo = (id, field, value) =>
    setNodos(prev => prev.map(n => n.id === id ? { ...n, [field]: value } : n))

  const updateServicio = (id, idx, value) =>
    setNodos(prev => prev.map(n => {
      if (n.id !== id) return n
      const s = [...n.servicios]; s[idx] = value
      return { ...n, servicios: s }
    }))

  const addNodo = () => {
    setNodos(prev => [...prev, newNodo(nextId)])
    setNextId(n => n + 1)
  }

  const removeNodo = (id) => {
    if (nodos.length <= 1) return
    setNodos(prev => prev.filter(n => n.id !== id))
  }

  // ── Estilos compartidos
  const inpStyle = {
    padding: '0.3rem 0.45rem', fontSize: '0.72rem',
    border: '1px solid #C8D8C0', borderRadius: '4px',
    background: 'white', fontFamily: "'IBM Plex Mono', monospace",
    boxSizing: 'border-box', color: '#2C2017',
  }
  const labelStyle = {
    fontSize: '0.58rem', fontWeight: '700', color: '#5A7A4A',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    display: 'block', marginBottom: '0.25rem',
  }

  return (
    <div style={{ marginBottom: '1.25rem', border: '2px solid #6A9A7A', borderRadius: '6px', overflow: 'hidden' }}>

      {/* ── Cabecera */}
      <div style={{ background: '#2E4A38', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <Bicycle size={15} weight="fill" style={{ color: '#A8D0B8', flexShrink: 0 }} />
        <span style={{ fontSize: '0.65rem', fontWeight: '700', color: '#A8D0B8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Calculadora BREEAM ES TRA 1 — Accesibilidad al Transporte Público
        </span>
      </div>

      <div style={{ padding: '0.875rem', background: '#F5FAF6' }}>

        {/* ── Config global */}
        <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Tipo de edificio</label>
            <select
              value={tipoEdificio}
              onChange={e => setTipoEdificio(e.target.value)}
              style={{ ...inpStyle, width: 'auto', border: '1px solid #6A9A7A' }}
            >
              {BUILDING_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Servicio de autobús dedicado</label>
            <select
              value={autobusDedicado ? 'Sí' : 'No'}
              onChange={e => setAutobusDedicado(e.target.value === 'Sí')}
              style={{ ...inpStyle, width: 'auto', border: '1px solid #6A9A7A' }}
            >
              <option>No</option>
              <option>Sí</option>
            </select>
          </div>
          {autobusDedicado && (
            <div style={{ fontSize: '0.65rem', color: '#5A7A4A', background: '#EAF3EC', border: '1px solid #A8D0B8', borderRadius: '4px', padding: '0.3rem 0.6rem', alignSelf: 'flex-end' }}>
              +1 pt si IA no alcanza puntuación
            </div>
          )}
        </div>

        {/* ── Nodos */}
        {nodos.map((nodo, ni) => {
          const transport  = TRANSPORT_TYPES.find(t => t.label === nodo.tipo) || TRANSPORT_TYPES[0]
          const d          = parseFloat(nodo.distancia)
          const isOver     = d > 0 && d > transport.maxDist
          const totalF     = nodo.servicios.reduce((s, f) => s + (parseFloat(f) || 0), 0)
          // Suma de frec_umbral del nodo (preview antes de rank logic global).
          const nodoIA     = (!isOver && d > 0)
            ? nodo.servicios.reduce((s, fStr) => {
                const f = parseFloat(fStr)
                return f > 0 ? s + calcFrecUmbral(d, f, transport.factor) : s
              }, 0)
            : 0
          const hasData    = d > 0 && totalF > 0

          return (
            <div key={nodo.id} style={{ marginBottom: '0.75rem', border: `1px solid ${isOver ? '#D4614A' : '#B8D4C0'}`, borderRadius: '5px', background: 'white', overflow: 'hidden' }}>

              {/* Header del nodo */}
              <div style={{ background: isOver ? '#FDF5F3' : '#EAF3EC', padding: '0.4rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: `1px solid ${isOver ? '#D4614A' : '#B8D4C0'}` }}>
                <span style={{ fontSize: '0.65rem', fontWeight: '700', color: isOver ? '#D4614A' : '#2E4A38', flex: 1 }}>
                  NODO {ni + 1}
                </span>
                {hasData && !isOver && (
                  <span style={{ fontSize: '0.6rem', color: '#3B5A47', fontFamily: "'IBM Plex Mono', monospace", background: '#D8EEE0', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>
                    IA contribución: {nodoIA.toFixed(3)}
                  </span>
                )}
                {isOver && (
                  <span style={{ fontSize: '0.6rem', color: '#D4614A', fontWeight: '700' }}>
                    ⚠ Excede dist. máx. ({transport.maxDist} m) — no computa
                  </span>
                )}
                {nodos.length > 1 && (
                  <button
                    onClick={() => removeNodo(nodo.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D4614A', fontSize: '1.1rem', lineHeight: 1, padding: '0 0.2rem' }}
                    title="Eliminar nodo"
                  >×</button>
                )}
              </div>

              {/* Body del nodo */}
              <div style={{ padding: '0.625rem 0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.875rem', marginBottom: '0.625rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>

                  {/* Tipo transporte */}
                  <div>
                    <label style={labelStyle}>Tipo de transporte</label>
                    <select
                      value={nodo.tipo}
                      onChange={e => updateNodo(nodo.id, 'tipo', e.target.value)}
                      style={{ ...inpStyle, width: 'auto' }}
                    >
                      {TRANSPORT_TYPES.map(t => <option key={t.label}>{t.label}</option>)}
                    </select>
                  </div>

                  {/* Distancia */}
                  <div>
                    <label style={labelStyle}>Distancia peatonal (m)</label>
                    <input
                      type="number" min="0" step="1"
                      value={nodo.distancia}
                      placeholder={`máx. ${transport.maxDist} m`}
                      onChange={e => updateNodo(nodo.id, 'distancia', e.target.value)}
                      style={{ ...inpStyle, width: '110px', borderColor: isOver ? '#D4614A' : '#C8D8C0' }}
                    />
                  </div>

                  {/* Info factor K */}
                  <div style={{ fontSize: '0.58rem', color: '#7A9A6A', alignSelf: 'flex-end', paddingBottom: '0.35rem' }}>
                    Factor: {transport.k} · máx. {transport.maxDist} m
                  </div>
                </div>

                {/* Servicios */}
                <div>
                  <label style={labelStyle}>Frecuencia media por hora — servicios/h (hasta 10 líneas)</label>
                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                    {nodo.servicios.map((f, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.55rem', color: '#9A8A7A', marginBottom: '0.12rem' }}>S{i + 1}</div>
                        <input
                          type="number" min="0" step="0.5"
                          value={f}
                          onChange={e => updateServicio(nodo.id, i, e.target.value)}
                          placeholder="—"
                          style={{ ...inpStyle, width: '44px', textAlign: 'center', padding: '0.3rem 0.2rem' }}
                        />
                      </div>
                    ))}
                  </div>
                  {totalF > 0 && (
                    <div style={{ marginTop: '0.3rem', fontSize: '0.6rem', color: '#5A7A4A' }}>
                      Total frecuencia nodo: <strong style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{totalF} servicios/h</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        {/* ── Añadir nodo */}
        {nodos.length < 20 && (
          <button
            onClick={addNodo}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: '1px dashed #7AB88A', borderRadius: '5px', padding: '0.4rem 0.75rem', cursor: 'pointer', color: '#3B6A4A', fontSize: '0.725rem', fontWeight: '600', width: '100%', justifyContent: 'center', marginBottom: '1rem', fontFamily: "'DM Sans', sans-serif" }}
          >
            <Plus size={13} /> Añadir nodo de transporte
          </button>
        )}

        {/* ── Resultados */}
        <div style={{ background: 'white', border: '1px solid #B8D4C0', borderRadius: '6px', padding: '0.875rem' }}>

          {/* Valores principales */}
          <div style={{ display: 'flex', gap: '0.875rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.875rem' }}>
            <div style={{ background: '#EAF3EC', border: '2px solid #2E4A38', borderRadius: '6px', padding: '0.5rem 1.25rem', textAlign: 'center', minWidth: '110px' }}>
              <div style={{ fontSize: '0.55rem', fontWeight: '700', color: '#2E4A38', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.2rem' }}>Índice de Accesibilidad</div>
              <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1A3020', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>{ia.toFixed(2)}</div>
            </div>

            <div style={{ background: '#3B2F2F', borderRadius: '6px', padding: '0.5rem 1.25rem', textAlign: 'center', minWidth: '90px' }}>
              <div style={{ fontSize: '0.55rem', fontWeight: '700', color: '#C98A2E', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.2rem' }}>Puntos TRA 1</div>
              <div style={{ fontSize: '1.6rem', fontWeight: '800', color: 'white', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
                {pts}<span style={{ fontSize: '0.8rem', color: '#DDD5C8' }}>/5</span>
              </div>
            </div>

            {hasBusBonus && (
              <div style={{ fontSize: '0.68rem', color: '#7A5A30', background: '#FDF5E8', border: '1px solid #DDB870', borderRadius: '6px', padding: '0.5rem 0.75rem', maxWidth: '200px' }}>
                <strong>+1 pt</strong> por autobús dedicado<br/>
                <span style={{ color: '#9A7A50', fontSize: '0.62rem' }}>(IA por debajo del umbral mínimo)</span>
              </div>
            )}
          </div>

          {/* Escala Tabla 25 */}
          <div style={{ marginBottom: '0.875rem' }}>
            <div style={{ fontSize: '0.58rem', fontWeight: '700', color: '#5A7A4A', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
              Tabla 25 — {tipoEdificio}
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              {(SCALE_LABELS[tipoEdificio] || []).map(([thr, p]) => {
                const active = ia >= thr
                return (
                  <div key={thr} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.65rem', padding: '0.2rem 0.45rem', borderRadius: '4px', background: active ? '#2E4A38' : '#EDF3EA', color: active ? '#A8D0B8' : '#6A8A72', fontWeight: active ? '700' : '400', border: `1px solid ${active ? '#2E4A38' : '#B8D4C0'}`, transition: 'all 0.2s' }}>
                    ≥{thr} → {p} pt{p !== 1 ? 's' : ''}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Guardar */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={saveNow} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.35rem 0.75rem', background: 'transparent',
              color: savedOk ? '#8D9A2E' : '#7A6A5A',
              border: `1px solid ${savedOk ? '#8D9A2E' : '#DDD5C8'}`,
              borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem',
              fontFamily: "'DM Sans', sans-serif"
            }}>
              <FloppyDisk size={14} />{savedOk ? '✓ Guardado' : 'Guardar datos'}
            </button>
            <button
              onClick={() => onSave({ ia, pts, tipoEdificio, autobusDedicado, nodos })}
              disabled={saving}
              style={{ background: '#3B2F2F', color: '#F5F0EA', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.775rem', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: "'DM Sans', sans-serif" }}
            >
              <FloppyDisk size={14} />
              {saving ? 'Guardando…' : `Guardar puntuación TRA 1 (${pts} pts)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
